import { BaseApiClient } from './base-client';

export interface Group {
  name: string;
  description?: string;
  member_count: number;
  notes?: string;
  leader?: string;
  created_at?: string;
}

export interface GroupCreate {
  name: string;
  description?: string;
  notes?: string;
  leader?: string;
}

export interface GroupUpdate {
  description?: string;
  notes?: string;
  leader?: string;
}

export interface GroupMember {
  username: string;
  primary_group: boolean;
  supplementary_groups: string[];
}

export interface User {
  username: string;
  uid?: number;
  groups?: string[];
  email?: string;
  first_name?: string;
  last_name?: string;
  department?: string;
  notes?: string;
  is_active?: boolean;
  vpn_ip?: string;
  created_at?: string;
}

export interface UserCreate {
  username: string;
  password: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  department?: string;
  notes?: string;
  samba_access?: boolean;
  ftp_access?: boolean;
  access_schedule?: string;
  account_expiry_date?: string;
}

export class UserGroupsApiClient extends BaseApiClient {
  // User management
  async listUsers(serviceId: number): Promise<User[]> {
    return this.get<User[]>(`/api/v1/user-groups/${serviceId}/users`);
  }

  async createUser(serviceId: number, user: UserCreate): Promise<User> {
    return this.post<User>(`/api/v1/user-groups/${serviceId}/users`, user);
  }

  async deleteUser(serviceId: number, username: string): Promise<{ status: string }> {
    return this.delete<{ status: string }>(`/api/v1/user-groups/${serviceId}/users/${username}`);
  }
  
  async updateUser(serviceId: number, username: string, updates: Partial<UserCreate>): Promise<{ status: string }> {
    return this.patch<{ status: string }>(`/api/v1/user-groups/${serviceId}/users/${username}`, updates);
  }
  
  async changeUserPassword(serviceId: number, username: string, data: { new_password: string }): Promise<{ status: string }> {
    return this.patch<{ status: string }>(`/api/v1/user-groups/${serviceId}/users/${username}/password`, data);
  }
  
  async activateUser(serviceId: number, username: string): Promise<{ status: string }> {
    return this.patch<{ status: string }>(`/api/v1/user-groups/${serviceId}/users/${username}/activate`);
  }
  
  async deactivateUser(serviceId: number, username: string): Promise<{ status: string }> {
    return this.patch<{ status: string }>(`/api/v1/user-groups/${serviceId}/users/${username}/deactivate`);
  }
  // Group management
  async listGroups(serviceId: number): Promise<Group[]> {
    return this.get<Group[]>(`/api/v1/user-groups/${serviceId}/groups`);
  }

  async createGroup(serviceId: number, group: GroupCreate): Promise<Group> {
    return this.post<Group>(`/api/v1/user-groups/${serviceId}/groups`, group);
  }

  async updateGroup(serviceId: number, groupName: string, updates: GroupUpdate): Promise<Group> {
    return this.patch<Group>(`/api/v1/user-groups/${serviceId}/groups/${groupName}`, updates);
  }

  async deleteGroup(serviceId: number, groupName: string): Promise<{ status: string }> {
    return this.delete<{ status: string }>(`/api/v1/user-groups/${serviceId}/groups/${groupName}`);
  }

  // Group member management
  async listGroupMembers(serviceId: number, groupName: string): Promise<GroupMember[]> {
    return this.get<GroupMember[]>(`/api/v1/user-groups/${serviceId}/groups/${groupName}/members`);
  }
  
  // Get group members as simple string array (for UI compatibility)
  async getGroupMembers(serviceId: number, groupName: string): Promise<string[]> {
    const response = await this.get<any>(`/api/v1/user-groups/${serviceId}/groups/${groupName}/members`);
    // Handle both old API format (array of strings) and new format (array of objects)
    if (Array.isArray(response)) {
      if (response.length === 0) return [];
      if (typeof response[0] === 'string') {
        return response;
      }
      return response.map(member => member.username || member);
    }
    return [];
  }

  async addGroupMember(serviceId: number, groupName: string, username: string, primary: boolean = false): Promise<{ status: string }> {
    return this.post<{ status: string }>(
      `/api/v1/user-groups/${serviceId}/groups/${groupName}/members/${username}`,
      null,
      { params: { primary } }
    );
  }
  
  // Add user to group (simplified method name for UI)
  async addUserToGroup(serviceId: number, groupName: string, username: string): Promise<{ status: string }> {
    return this.addGroupMember(serviceId, groupName, username);
  }

  async removeGroupMember(serviceId: number, groupName: string, username: string): Promise<{ status: string }> {
    return this.delete<{ status: string }>(`/api/v1/user-groups/${serviceId}/groups/${groupName}/members/${username}`);
  }
  
  // Remove user from group (simplified method name for UI)
  async removeUserFromGroup(serviceId: number, groupName: string, username: string): Promise<{ status: string }> {
    return this.removeGroupMember(serviceId, groupName, username);
  }

  // User groups
  async getUserGroups(serviceId: number, username: string): Promise<GroupMember> {
    return this.get<GroupMember>(`/api/v1/user-groups/${serviceId}/users/${username}/groups`);
  }

  // Sync
  async syncGroups(serviceId: number): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/user-groups/${serviceId}/sync`);
  }
  
}